import { pick } from 'lodash';

import { MessagesAPI } from './messages-api';
import { useAssistantCloudStore } from './stores/assistant-cloud-store';
import { ensureStoreInitialized } from './utils';

import type {
  ThreadData,
  ThreadCreateInput,
  ThreadUpdateInput,
  ThreadListResponse,
  ThreadCreateResponse,
} from './types';

const THREAD_FIELDS = [
  'id',
  'external_id',
  'title',
  'is_archived',
  'created_at',
  'updated_at',
  'last_message_at',
] as const;

export class ThreadsAPI {
  public messages: MessagesAPI;

  constructor() {
    this.messages = new MessagesAPI();
  }

  async list(): Promise<ThreadListResponse> {
    await ensureStoreInitialized();
    const { getSortedThreads } = useAssistantCloudStore.getState();
    const threads = getSortedThreads();
    return { threads: threads.map(t => pick(t, THREAD_FIELDS)) };
  }

  async create(data: ThreadCreateInput): Promise<ThreadCreateResponse> {
    await ensureStoreInitialized();
    const { createThread } = useAssistantCloudStore.getState();
    const threadId = await createThread(data);
    return { thread_id: threadId };
  }

  async update(threadId: string, data: ThreadUpdateInput): Promise<void> {
    await ensureStoreInitialized();
    const { updateThread } = useAssistantCloudStore.getState();
    await updateThread(threadId, data);
  }

  async delete(threadId: string): Promise<void> {
    await ensureStoreInitialized();
    const { deleteThread } = useAssistantCloudStore.getState();
    await deleteThread(threadId);
  }

  async get(threadId: string): Promise<ThreadData | null> {
    await ensureStoreInitialized();
    const { getThread } = useAssistantCloudStore.getState();
    const thread = getThread(threadId);
    return thread ? pick(thread, THREAD_FIELDS) : null;
  }
}
